import { supabase } from "@/integrations/supabase/client";
import { googleCalendarService } from "./google-calendar/google-calendar-service";
import { createBudgetEventSnapshot } from "@/lib/budget-version-snapshot";

export interface Event {
  id: string;
  client_name: string;
  groom_name?: string | null;
  bride_name?: string | null;
  event_name?: string;
  phone?: string;
  email?: string;
  date: string;
  event_time?: string;
  duration_hours?: number | null;
  event_location?: string;
  city?: string;
  event_type: string;
  guests: number;
  drinks?: string[];
  notes?: string;
  status: string;
  origin?: "manual" | "public_budget_form";
  lead_source?: string;
  referral_name?: string;
  is_paid_full: boolean;
  payment_due_date?: string;
  payment_method?: string;
  payment_percent_received?: number;
  paid_amount_received?: number | null;
  current_budget_value?: number;
  current_profit_value?: number;
  /** Derived from persisted event_budget_versions rows; never stored on events. */
  has_budget_version?: boolean;
  google_calendar_event_id?: string | null;
  google_calendar_sync_status?: "not_synced" | "pending" | "synced" | "error" | "cancelled";
  google_calendar_synced_at?: string | null;
  google_calendar_sync_error?: string | null;
  google_calendar_html_link?: string | null;
  created_at: string;
  updated_at: string;
}

export interface BudgetVersion {
  id: string;
  event_id: string;
  version_number: number;
  is_current: boolean;
  status: string;
  guest_count: number | null;
  event_snapshot: Record<string, unknown> | null;

  // Budget Details
  selected_drinks: any;
  beverages: any;
  drinks_per_person: number;
  drinks_markup_percentage: number;
  drinks_cost_sum: number;
  average_drink_cost: number;
  drinks_base_cost: number;
  drinks_final_value: number;
  has_welcome_drinks: boolean;
  welcome_drinks_per_person: number;
  welcome_drinks_profit_percentage: number;
  welcome_drinks_selected: any;
  welcome_drinks_cost: number;
  welcome_drinks_final_value: number;
  has_shots: boolean;
  shots_items: any;
  shots_total_value: number;

  // Staff
  bartender_quantity: number;
  bartender_unit_value: number;
  keeper_quantity: number;
  keeper_unit_value: number;
  copeira_quantity: number;
  copeira_unit_value: number;
  team_total_value: number;

  // Ice
  ice_packages_quantity: number;
  ice_package_unit_value: number;
  ice_total_value: number;

  // Travel
  has_travel: boolean;
  fuel_value: number;

  // Misc
  miscellaneous_items: any;
  miscellaneous_total_value: number;

  // Profit & Final
  discount_value: number;
  discount_description?: string;
  profit_value: number;
  final_budget_value: number;
  average_value_per_person: number;

  // Payment
  payment_method?: string;
  paid_percentage: number;
  paid_value: number;
  pending_percentage: number;
  pending_value: number;
  pending_payment_date?: string;

  created_at: string;
  updated_at: string;
}

export interface BudgetHistory {
  id: string;
  event_id: string;
  budget_version_id?: string;
  action: string;
  changed_fields?: any;
  previous_data?: any;
  new_data?: any;
  previous_final_value?: number;
  new_final_value?: number;
  created_at: string;
}

export interface NegotiationHistory {
  id: string;
  event_id: string;
  status: string;
  note?: string;
  created_at: string;
}

export interface EventPlanningItem {
  id?: string;
  event_id: string;
  source_expense_item_id?: string;
  inventory_id?: string;
  item_name: string;
  category: string;
  planned_quantity: number;
  unit?: string;
  estimated_unit_cost: number;
  estimated_total_cost: number;
  origin?: string;
  notes?: string;
}

export interface EventClosingItem {
  id?: string;
  event_id: string;
  planning_item_id?: string;
  item_name: string;
  category: string;
  quantity_taken: number;
  quantity_used: number;
  quantity_returned: number;
  quantity_lost_or_broken: number;
  unit?: string;
  unit_cost: number;
  consumed_cost: number;
  lost_cost: number;
  notes?: string;
}

export interface EventClosing {
  id?: string;
  event_id: string;
  closing_date?: string;
  revenue_amount: number;
  total_purchase_cost: number;
  total_team_cost: number;
  total_logistics_cost: number;
  total_consumed_cost: number;
  total_lost_cost: number;
  total_event_cost: number;
  event_profit: number;
  event_margin: number;
  general_notes?: string;
  improvement_points?: string;
  status: string;
}

export const eventBudgetService = {
  subscribeToEventChanges(onChange: () => void) {
    return supabase
      .channel("event-list-changes")
      .on("postgres_changes", { event: "*", schema: "public", table: "events" }, onChange)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "event_budget_versions" },
        onChange,
      )
      .subscribe();
  },
  // --- Events ---
  async listEvents() {
    const { data, error } = await supabase
      .from("events")
      .select("*")
      .order("date", { ascending: true });
    if (error) throw error;

    const events = (data || []) as Event[];
    if (events.length === 0) return events;

    const { data: persistedBudgets, error: budgetError } = await supabase
      .from("event_budget_versions")
      .select(
        "event_id, is_current, profit_value, paid_percentage, pending_payment_date, final_budget_value, discount_value, discount_description",
      );

    if (budgetError) {
      console.warn(
        "Falha ao carregar versões atuais de orçamento. Usando dados base de events.",
        budgetError,
      );
      return events;
    }

    const eventIdsWithBudget = new Set((persistedBudgets || []).map((b: any) => b.event_id));
    const budgetByEvent = new Map(
      (persistedBudgets || []).filter((b: any) => b.is_current).map((b: any) => [b.event_id, b]),
    );

    return events.map((event) => {
      const budget = budgetByEvent.get(event.id);
      if (!budget) return { ...event, has_budget_version: eventIdsWithBudget.has(event.id) };

      const discount = Number(budget.discount_value || 0);
      const discountDescription = budget.discount_description;

      // Parse detailed discounts to separate drink discounts from regular discounts
      let drinkDiscount = 0;
      if (discountDescription) {
        try {
          const parsed = JSON.parse(discountDescription);
          if (Array.isArray(parsed?.descontos)) {
            drinkDiscount = parsed.descontos
              .filter((d: any) => !!d.deduzirCustoDrinks)
              .reduce((acc: number, d: any) => acc + (Number(d.valor) || 0), 0);
          }
        } catch {}
      }

      const regularDiscount = Math.max(0, discount - drinkDiscount);
      const budgetProfit = Number(budget.profit_value || 0);
      const reconciledProfit = budgetProfit - regularDiscount;

      return {
        ...event,
        has_budget_version: true,
        current_budget_value: Number(budget.final_budget_value || event.current_budget_value || 0),
        current_profit_value: reconciledProfit,
        payment_percent_received: Number(
          budget.paid_percentage ?? event.payment_percent_received ?? 0,
        ),
        payment_due_date: budget.pending_payment_date || event.payment_due_date,
      };
    });
  },

  async getBudgetVersionById(id: string) {
    const { data, error } = await supabase
      .from("event_budget_versions")
      .select("*")
      .eq("id", id)
      .single();
    if (error) throw error;
    return data;
  },

  // --- Event Planning Items ---
  async getPlanningItems(eventId: string) {
    const { data, error } = await supabase
      .from("event_planning_items")
      .select("*")
      .eq("event_id", eventId);
    if (error) throw error;
    return data as EventPlanningItem[];
  },

  async addPlanningItems(items: EventPlanningItem[]) {
    const { data, error } = await supabase.from("event_planning_items").insert(items).select();
    if (error) throw error;
    return data as EventPlanningItem[];
  },

  async updatePlanningItem(id: string, payload: Partial<EventPlanningItem>) {
    const { data, error } = await supabase
      .from("event_planning_items")
      .update(payload)
      .eq("id", id)
      .select()
      .single();
    if (error) throw error;
    return data as EventPlanningItem;
  },

  async deletePlanningItem(id: string) {
    const { error } = await supabase.from("event_planning_items").delete().eq("id", id);
    if (error) throw error;
  },

  async returnPlanningItemLeftoverToInventory(planningItemId: string, quantity: number, notes?: string) {
    const { data, error } = await (supabase as any).rpc("return_event_leftover_to_inventory", {
      p_planning_item_id: planningItemId,
      p_quantity: quantity,
      p_notes: notes || null,
    });
    if (error) throw error;
    return data as string;
  },

  // --- Event Closing ---
  async getEventClosing(eventId: string) {
    const { data, error } = await supabase
      .from("event_closings")
      .select("*")
      .eq("event_id", eventId)
      .maybeSingle();
    if (error) throw error;
    return data as EventClosing | null;
  },

  async upsertEventClosing(payload: Partial<EventClosing>) {
    // Check if exists
    if (!payload.event_id) throw new Error("Missing event_id");
    const existing = await this.getEventClosing(payload.event_id);

    if (existing && existing.id) {
      const { data, error } = await supabase
        .from("event_closings")
        .update(payload)
        .eq("id", existing.id)
        .select()
        .single();
      if (error) throw error;
      return data as EventClosing;
    } else {
      const { data, error } = await supabase
        .from("event_closings")
        .insert({ ...payload, event_id: payload.event_id! } as any)
        .select()
        .single();
      if (error) throw error;
      return data as EventClosing;
    }
  },

  async getClosingItems(eventId: string) {
    const { data, error } = await supabase
      .from("event_closing_items")
      .select("*")
      .eq("event_id", eventId);
    if (error) throw error;
    return data as EventClosingItem[];
  },

  async upsertClosingItems(items: EventClosingItem[]) {
    const { data, error } = await supabase.from("event_closing_items").upsert(items).select();
    if (error) throw error;
    return data as EventClosingItem[];
  },

  async getEventById(id: string) {
    const { data, error } = await supabase.from("events").select("*").eq("id", id).limit(1);
    if (error) throw error;
    return data && data.length > 0 ? (data[0] as Event) : null;
  },

  async createEvent(
    payload: Partial<Event> & Pick<Event, "client_name" | "date" | "event_type" | "guests">,
  ) {
    const { data, error } = await supabase
      .from("events")
      .insert(payload as any)
      .select()
      .single();
    if (error) throw error;
    return data as Event;
  },

  async updateEvent(id: string, payload: Partial<Event>) {
    const { data, error } = await supabase
      .from("events")
      .update({ ...payload, updated_at: new Date().toISOString() } as any)
      .eq("id", id)
      .select()
      .single();
    if (error) throw error;

    // Trigger Google Calendar sync asynchronously for relevant updates
    const isRelevantChange =
      payload.status !== undefined ||
      payload.date !== undefined ||
      payload.event_time !== undefined ||
      payload.event_location !== undefined ||
      payload.event_name !== undefined ||
      payload.client_name !== undefined ||
      payload.guests !== undefined;

    if (isRelevantChange) {
      googleCalendarService.syncEvent(id).catch((syncErr) => {
        console.warn("[eventBudgetService] Google Calendar background sync error:", syncErr);
      });
    }

    return data as Event;
  },

  async deleteEvent(id: string) {
    const { error } = await supabase.from("events").delete().eq("id", id);
    if (error) throw error;
  },

  async deletePublicBudgetRequest(eventId: string) {
    const { data, error } = await supabase.rpc("delete_public_budget_request", {
      p_event_id: eventId,
    });
    if (error) throw new Error(error.message);

    const result = data as { deleted?: boolean; reason?: string; message?: string } | null;
    if (!result?.deleted) {
      throw new Error(result?.message || result?.reason || "A solicitação não foi excluída.");
    }
  },

  async checkEventsSameDate(date: string) {
    const { data, error } = await supabase.from("events").select("*").eq("date", date);
    if (error) throw error;
    return data as Event[];
  },

  // --- Budgets ---
  async getCurrentBudget(eventId: string) {
    const { data, error } = await supabase
      .from("event_budget_versions")
      .select("*")
      .eq("event_id", eventId)
      .eq("is_current", true)
      .limit(1);
    if (error) throw error;
    return data && data.length > 0 ? (data[0] as unknown as BudgetVersion) : null;
  },

  async listBudgetVersions(eventId: string) {
    const { data, error } = await supabase
      .from("event_budget_versions")
      .select("*")
      .eq("event_id", eventId)
      .order("version_number", { ascending: false });
    if (error) throw error;
    return data as unknown as BudgetVersion[];
  },

  async createBudgetVersion(eventId: string, payload: any, _isNewVersion: boolean = true) {
    // A persisted version is never edited in place. Even the regular "save"
    // flow appends a snapshot, so loading an old version cannot rewrite it.
    let versionNumber = 1;
    const { data: latest } = await supabase
      .from("event_budget_versions")
      .select("version_number")
      .eq("event_id", eventId)
      .order("version_number", { ascending: false })
      .limit(1)
      .maybeSingle();

    if (latest) {
      versionNumber = latest.version_number + 1;
    }

    // Set all others to NOT current
    await supabase
      .from("event_budget_versions")
      .update({ is_current: false })
      .eq("event_id", eventId);

    const event = await this.getEventById(eventId);
    if (!event) throw new Error("Evento não encontrado ao criar a versão do orçamento.");
    const guestCount = Number(payload.guest_count ?? event.guests);
    if (!Number.isFinite(guestCount) || guestCount < 0) {
      throw new Error("Quantidade de convidados inválida para a versão do orçamento.");
    }

    const { data, error } = await supabase
      .from("event_budget_versions")
      .insert({
        ...payload,
        guest_count: guestCount,
        event_snapshot: payload.event_snapshot ?? createBudgetEventSnapshot(event as any),
        event_id: eventId,
        version_number: versionNumber,
        is_current: true,
        updated_at: new Date().toISOString(),
      })
      .select()
      .single();

    if (error) throw error;
    return data as unknown as BudgetVersion;
  },

  async deleteBudgetVersion(budgetId: string) {
    // Delete history first (if no cascade in DB)
    await supabase.from("event_budget_history").delete().eq("budget_version_id", budgetId);

    const { error } = await supabase.from("event_budget_versions").delete().eq("id", budgetId);
    if (error) throw error;
  },

  async updateBudgetDraft(budgetId: string, payload: any) {
    const { data, error } = await supabase
      .from("event_budget_versions")
      .update({ ...payload, updated_at: new Date().toISOString() })
      .eq("id", budgetId)
      .select()
      .single();
    if (error) throw error;
    return data as unknown as BudgetVersion;
  },

  async setCurrentVersion(eventId: string, budgetId: string) {
    await supabase
      .from("event_budget_versions")
      .update({ is_current: false })
      .eq("event_id", eventId);

    const { data, error } = await supabase
      .from("event_budget_versions")
      .update({ is_current: true })
      .eq("id", budgetId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // --- History ---
  async addBudgetHistory(payload: Omit<BudgetHistory, "id" | "created_at">) {
    const { data, error } = await supabase
      .from("event_budget_history")
      .insert(payload)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async getBudgetHistory(eventId: string) {
    const { data, error } = await supabase
      .from("event_budget_history")
      .select("*")
      .eq("event_id", eventId)
      .order("created_at", { ascending: false });
    if (error) throw error;
    return data as BudgetHistory[];
  },

  // --- Negotiation ---
  async updateNegotiationStatus(eventId: string, status: string, note?: string) {
    // Update event status
    await this.updateEvent(eventId, { status });

    // Add to negotiation history
    const { data, error } = await supabase
      .from("event_negotiation_history")
      .insert({
        event_id: eventId,
        status,
        note,
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async getNegotiationHistory(eventId: string) {
    const { data, error } = await supabase
      .from("event_negotiation_history")
      .select("*")
      .eq("event_id", eventId)
      .order("created_at", { ascending: false });
    if (error) throw error;
    return data as NegotiationHistory[];
  },

  async updateNegotiationNote(noteId: string, note: string) {
    const { data, error } = await supabase
      .from("event_negotiation_history")
      .update({ note })
      .eq("id", noteId)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async deleteNegotiationNote(noteId: string) {
    const { error } = await supabase.from("event_negotiation_history").delete().eq("id", noteId);
    if (error) throw error;
  },

  async createLegacyEvent(payload: {
    client_name: string;
    event_type: string;
    date: string;
    city: string;
    guests: number;
    drinks: string[];
    final_budget_value: number;
    paid_value: number;
    average_drink_cost: number;
  }) {
    const drinksBaseCost = payload.average_drink_cost * payload.guests * 4;
    const profitValue = Math.max(0, payload.final_budget_value - drinksBaseCost);
    const paidPercentage =
      payload.final_budget_value > 0
        ? Math.min(100, Math.round((payload.paid_value / payload.final_budget_value) * 100))
        : 0;

    const eventPayload = {
      client_name: payload.client_name,
      event_name: payload.client_name,
      event_type: payload.event_type,
      date: payload.date,
      city: payload.city,
      guests: payload.guests,
      drinks: payload.drinks,
      status: "finalizado",
      current_budget_value: payload.final_budget_value,
      current_profit_value: profitValue,
      payment_percent_received: paidPercentage,
      is_paid_full: payload.paid_value >= payload.final_budget_value,
    };

    const { data: event, error: eventError } = await supabase
      .from("events")
      .insert(eventPayload)
      .select()
      .single();

    if (eventError) throw eventError;

    const budgetPayload = {
      event_id: event.id,
      version_number: 1,
      is_current: true,
      status: "approved",
      guest_count: payload.guests,
      event_snapshot: createBudgetEventSnapshot(event as any) as any,
      selected_drinks: { ids: payload.drinks, copos: {} },
      drinks_per_person: 4,
      drinks_markup_percentage: 0,
      drinks_cost_sum: payload.average_drink_cost * payload.drinks.length,
      average_drink_cost: payload.average_drink_cost,
      drinks_base_cost: drinksBaseCost,
      drinks_final_value: drinksBaseCost,
      bartender_quantity: 0,
      bartender_unit_value: 200,
      keeper_quantity: 0,
      keeper_unit_value: 200,
      copeira_quantity: 0,
      copeira_unit_value: 200,
      team_total_value: 0,
      ice_packages_quantity: 0,
      ice_package_unit_value: 6,
      ice_total_value: 0,
      has_travel: false,
      fuel_value: 0,
      miscellaneous_items: [],
      miscellaneous_total_value: 0,
      discount_value: 0,
      profit_value: profitValue,
      final_budget_value: payload.final_budget_value,
      average_value_per_person:
        payload.guests > 0 ? payload.final_budget_value / payload.guests : 0,
      paid_percentage: paidPercentage,
      paid_value: payload.paid_value,
      pending_percentage: 100 - paidPercentage,
      pending_value: Math.max(0, payload.final_budget_value - payload.paid_value),
      updated_at: new Date().toISOString(),
    };

    const { data: budget, error: budgetError } = await supabase
      .from("event_budget_versions")
      .insert(budgetPayload as any)
      .select()
      .single();

    if (budgetError) throw budgetError;

    return { event, budget };
  },
};
